import { Injectable, inject } from '@angular/core';

import { environment } from '@env/environment';

import { AuthService } from './auth.service';

/**
 * Development-only auth service that simulates JWT authentication.
 * Generates a mock JWT token with proper claims for the Donation Portal.
 * Replace with real Company SSO integration in production.
 */
@Injectable({ providedIn: 'root' })
export class DevAuthService {
  private readonly authService = inject(AuthService);

  private assertDevOnly(): void {
    if (environment.production) {
      throw new Error('DevAuthService must not be used in production. Use real authentication instead.');
    }
  }

  /**
   * Simulate donation portal login — clears any existing session
   * and creates a JWT with user claims.
   */
  loginAsDonationPortal(username: string): void {
    this.assertDevOnly();
    this.authService.clearAuthData();

    const token = this.createMockJwt({
      userid: '1',
      companyid: '1',
      email: `${username.toLowerCase()}@brijjworks.com`,
      fullname: username,
      role: 'user',
      phonenumber: '(555) 100-0000'
    });

    this.authService.setTokens(token, `refresh-${Date.now()}`, `session-${Date.now()}`);
  }

  /**
   * Clear simulated auth session.
   */
  logout(): void {
    this.authService.clearAuthData();
  }

  /**
   * Create a mock JWT token with standard claims.
   * Uses base64url encoding to create a valid JWT structure that jwt-decode can parse.
   * Token expires in 24 hours.
   */
  private createMockJwt(claims: {
    userid: string;
    companyid: string;
    email: string;
    fullname: string;
    role: string;
    phonenumber: string;
  }): string {
    const header = { alg: 'HS256', typ: 'JWT' };
    const now = Math.floor(Date.now() / 1000);
    const payload = {
      sub: claims.userid,
      'custom:userid': claims.userid,
      'custom:companyid': claims.companyid,
      'custom:email': claims.email,
      'custom:fullname': claims.fullname,
      'custom:role': [claims.role],
      'custom:phonenumber': claims.phonenumber,
      'custom:usertype': 'AppUser',
      'custom:userorigin': 'Internal',
      'custom:timezonecode': 'UTC',
      'custom:mustchangepasswordonlogin': 'False',
      iss: 'dev-auth-simulator',
      aud: 'donation-app',
      iat: now,
      nbf: now,
      exp: now + 86400 // 24 hours
    };

    const encode = (obj: object): string =>
      btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    return `${encode(header)}.${encode(payload)}.dev-signature`;
  }
}
